interface HeaderSearchResponse {
    suggestions: string[];
    categories: { id: string; title: string }[];
    products: { oxid: string; artnum: string; title: string; thumbnailUrl: string; customerPartNo: string | null }[];
}

export function initHeaderSearchAutocomplete(): void {
    const input       = document.getElementById('searchParam') as HTMLInputElement | null;
    const dropdown    = document.getElementById('headerSearchDropdown') as HTMLDivElement | null;
    const ghost       = document.getElementById('headerSearchGhost') as HTMLDivElement | null;
    const searchForm  = document.getElementById('searchForm') as HTMLFormElement | null;

    if (!input || !dropdown || !ghost) return;

    const ghostTyped      = ghost.querySelector<HTMLSpanElement>('.header-search__ghost-typed');
    const ghostCompletion = ghost.querySelector<HTMLSpanElement>('.header-search__ghost-completion');
    const suggestionsList = dropdown.querySelector<HTMLUListElement>('.header-search-panel__suggestions');
    const categoriesList  = dropdown.querySelector<HTMLUListElement>('.header-search-panel__categories');
    const productsGrid    = dropdown.querySelector<HTMLDivElement>('.header-search-panel__products');

    if (!ghostTyped || !ghostCompletion || !suggestionsList || !categoriesList || !productsGrid) return;

    const customerArtnumLabel = searchForm?.dataset.customerArtnumLabel ?? '';

    let debounceTimer: ReturnType<typeof setTimeout>;
    let requestId = 0;
    let activeIndex = -1;
    let currentGhostCompletion = '';
    let lastData: HeaderSearchResponse = { suggestions: [], categories: [], products: [] };

    function closeDropdown(): void {
        dropdown!.hidden = true;
        activeIndex = -1;
    }

    function openDropdown(): void {
        dropdown!.hidden = false;
    }

    function renderGhost(typed: string, completion: string): void {
        ghostTyped!.textContent = typed;
        ghostCompletion!.textContent = completion;
        currentGhostCompletion = completion;
    }

    function computeGhostCompletion(typed: string, data: HeaderSearchResponse): string {
        if (!typed) return '';
        const candidate = data.products[0]?.title ?? data.suggestions[0] ?? '';
        if (!candidate || !candidate.toLowerCase().startsWith(typed.toLowerCase())) return '';
        return candidate.slice(typed.length);
    }

    function highlightProduct(idx: number): void {
        const cards = productsGrid!.querySelectorAll<HTMLAnchorElement>('.header-search-panel__product-card');
        cards.forEach((el, i) => el.classList.toggle('header-search-panel__product-card--active', i === idx));
        activeIndex = idx;
    }

    function selectProduct(idx: number): void {
        const product = lastData.products[idx];
        if (!product) return;
        window.location.href = '?cl=details&anid=' + encodeURIComponent(product.oxid);
    }

    function selectSuggestion(text: string): void {
        input!.value = text;
        renderGhost(text, '');
        searchForm?.requestSubmit();
    }

    function renderPanel(data: HeaderSearchResponse): void {
        lastData = data;
        activeIndex = -1;

        suggestionsList!.innerHTML = '';
        data.suggestions.forEach((text) => {
            const li = document.createElement('li');
            li.textContent = text;
            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                selectSuggestion(text);
            });
            suggestionsList!.appendChild(li);
        });

        categoriesList!.innerHTML = '';
        data.categories.forEach((cat) => {
            const li = document.createElement('li');
            const a  = document.createElement('a');
            a.href = '?cl=alist&cnid=' + encodeURIComponent(cat.id);
            a.textContent = cat.title;
            li.appendChild(a);
            categoriesList!.appendChild(li);
        });

        productsGrid!.innerHTML = '';
        data.products.forEach((product) => {
            const a = document.createElement('a');
            a.className = 'header-search-panel__product-card';
            a.href = '?cl=details&anid=' + encodeURIComponent(product.oxid);

            const img = document.createElement('img');
            img.className = 'header-search-panel__product-thumb';
            img.src = product.thumbnailUrl;
            img.alt = '';
            img.loading = 'lazy';

            const info = document.createElement('div');
            info.className = 'header-search-panel__product-info';

            const title = document.createElement('div');
            title.className = 'header-search-panel__product-title';
            title.textContent = product.title;

            const artnum = document.createElement('div');
            artnum.className = 'header-search-panel__product-artnum';
            artnum.textContent = 'Artikelnummer: ' + product.artnum;

            info.append(title, artnum);

            if (product.customerPartNo) {
                const customerArtnum = document.createElement('div');
                customerArtnum.className = 'header-search-panel__product-customer-artnum';
                customerArtnum.textContent = customerArtnumLabel + ': ' + product.customerPartNo;
                info.append(customerArtnum);
            }

            a.append(img, info);
            productsGrid!.appendChild(a);
        });

        const hasResults = data.suggestions.length > 0 || data.categories.length > 0 || data.products.length > 0;
        if (hasResults) {
            openDropdown();
        } else {
            closeDropdown();
        }
    }

    async function fetchResults(q: string): Promise<void> {
        if (q.length < 1) {
            closeDropdown();
            renderGhost('', '');
            return;
        }

        const myRequestId = ++requestId;

        try {
            const res  = await fetch(`?cl=woehnersearch&fnc=search&q=${encodeURIComponent(q)}`);
            const data: HeaderSearchResponse = await res.json();

            if (myRequestId !== requestId) return; // superseded by a newer request

            renderPanel(data);
            renderGhost(input!.value, computeGhostCompletion(input!.value, data));
        } catch {
            if (myRequestId === requestId) closeDropdown();
        }
    }

    function acceptGhost(): void {
        if (!currentGhostCompletion) return;
        const completed = input!.value + currentGhostCompletion;
        input!.value = completed;
        input!.setSelectionRange(completed.length, completed.length);
        renderGhost(completed, '');
        clearTimeout(debounceTimer);
        fetchResults(completed);
    }

    input.addEventListener('input', () => {
        const val = input.value;
        renderGhost(val, ''); // clear stale ghost immediately; recomputed once fresh data arrives

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => fetchResults(val.trim()), 220);
    });

    input.addEventListener('keydown', (e) => {
        const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length;

        if ((e.key === 'ArrowRight' || e.key === 'End') && atEnd && currentGhostCompletion) {
            e.preventDefault();
            acceptGhost();
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const count = productsGrid!.querySelectorAll('.header-search-panel__product-card').length;
            highlightProduct(Math.min(activeIndex + 1, count - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            highlightProduct(Math.max(activeIndex - 1, 0));
        } else if (e.key === 'Enter' && activeIndex >= 0 && !dropdown!.hidden) {
            e.preventDefault();
            selectProduct(activeIndex);
        } else if (e.key === 'Escape') {
            closeDropdown();
        }
        // Enter with nothing highlighted falls through to the form's normal submit —
        // the existing full-page search must keep working exactly as before.
    });

    input.addEventListener('blur', () => {
        setTimeout(closeDropdown, 150);
    });

    document.addEventListener('click', (e) => {
        if (!input!.contains(e.target as Node) && !dropdown!.contains(e.target as Node)) {
            closeDropdown();
        }
    });
}
